import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { detailKey, listKey } from '../../api/queryKeys';
import { RecordScreen } from '../../components/RecordScreen';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumLabel, enumOptions, humanize } from '../../lib/enums';
import { formatDate, formatDateTime, formatHours, formatMoney, formatRateBp } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { AdjustmentsPanel } from './AdjustmentsPanel';

interface PayrollLine {
  id: string;
  staffId: string;
  staffName?: string | null;
  grossMinor: number;
  currency: string;
  hoursMinutes: number;
  hourlyRateMinor?: number | null;
  /** The contracted salary, per month — what the person is on, not what this period paid. */
  salaryMinor?: number | null;
  /**
   * What this period bought of that salary: the whole of it monthly, half of it twice a month, a
   * fortnight's share of it every two weeks. Absent on hourly lines, and on salaried registers
   * generated before the cadence was read — those paid a whole month whatever the period was.
   */
  periodSalaryMinor?: number | null;
  holidayWorkedMinutes?: number | null;
  holidayPremiumMinor?: number | null;
  leaveBenefitAdjustmentMinor?: number | null;
  leaveBenefitLines?: {
    leaveId: string;
    kind: string;
    employerDueMinor: number;
    ordinaryIncludedMinor: number;
    advanceMinor: number;
    adjustmentMinor: number;
  }[];
  overtimeMinutes?: number | null;
  overtimePremiumBp?: number | null;
  overtimePremiumMinor?: number | null;
  /** Signed net of the manual adjustments folded into gross; null when nothing manual applied. */
  adjustmentsMinor?: number | null;
  /** What was folded in, frozen at generation time so the gross can be reproduced from the page. */
  adjustmentLines?: {
    adjustmentId: string;
    kind: 'extraPayment' | 'unpaidTimeOff' | 'deduction';
    amountMinor: number;
    days: number | null;
    note: string;
  }[];
}

interface PayrollRun {
  id: string;
  payrollPeriodId: string;
  status?: string;
  createdAt?: string;
  /** Recorded when the register was generated, so it still reads true weeks later. */
  skipped?: { staffId: string; staffName?: string | null; reason: string }[];
  /** Who signed it off and when. Names, because a directory id resolves to nobody here. */
  finalizedAt?: string | null;
  finalizedByName?: string | null;
  paidAt?: string | null;
  paidMethod?: string | null;
  paidByName?: string | null;
}

interface RunResponse {
  run: PayrollRun;
  lines: PayrollLine[];
  period?: { periodStart: string; periodEnd: string; cadence: string } | null;
  /** A hire, termination, or employment-date correction happened after this draft was built. */
  needsRebuild: boolean;
}

/** Reasons the register leaves someone out or needs a second look, in words a manager can act on. */
const SKIP_REASON: Record<string, string> = {
  scheduleInputsChanged:
    'The working week changed. Rebuild and approve the affected hours, then rebuild this register.',
  holidayRateNeedsReview:
    'The ordinary wage changed during this period. Reconcile the applicable holiday wage before signing off pay.',
  holidayHoursNeedReview:
    'Rebuild and approve the holiday hours. Timeless or ambiguous shifts must be corrected before holiday pay can be signed off.',
  holidaySalaryRateNeedsReview:
    'Record the verified ordinary hourly wage for salary holiday work under Vacation & benefits, then rebuild.',
  leaveBenefitNeedsReview:
    'Approve the leave hours and verify that the benefit wage currency matches payroll, then rebuild.',
  hoursNotApproved: 'Their hours have not been approved yet — approve them, then rebuild this register.',
  noCompensation: 'No pay rate on file — add one and generate the register again.',
  terminationDateMissing:
    'They are recorded as no longer employed but with no last day, so their final pay cannot be worked out. Put the date on their record and rebuild this register.',
  noHoursRecorded: 'No hours were clocked in this period.',
  unclosedShift: 'A shift was never clocked out, so those hours are not counted yet.',
  overtimePremiumNotConfigured:
    'They worked overtime and are owed a premium, but no overtime rate has been set. The base hours are paid; the extra is not.',
  overtimeRateUnknownSalaried:
    'They are on a salary and marked for paid overtime, but there is no agreed hourly rate to base the extra on.',
  adjustmentCurrencyMismatch:
    'A manual adjustment for them is in a different currency than their pay, so it was not applied. Re-enter it in the currency they are paid in, then rebuild.',
  adjustmentsExceedPay:
    'Their deductions add up to more than this period pays them, so no line was written — a register cannot owe the business money. Reduce the adjustment or spread it across periods, then rebuild.',
  adjustmentWithoutLine:
    'A manual adjustment is recorded for them but they have no pay line for it to join. Sort out why they are not on the register, then rebuild.',
};

/**
 * The gross-pay register, which previously existed only as a line count in a toast that faded.
 *
 * Payroll is the highest-stakes screen in the business and it produced nothing anyone could read,
 * check, or hand to an accountant. Everything here was already being computed and written to the
 * database — none of it had a way back out.
 */
export function PayrollRunPage() {
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const runQuery = useQuery({
    queryKey: detailKey('/admin/v1/payroll/runs', runId),
    queryFn: () => rawApi<RunResponse>('GET', `/admin/v1/payroll/runs/${runId}`),
    enabled: Boolean(runId),
  });

  const data = runQuery.data;
  const lines = data?.lines ?? [];
  const status = data?.run.status ?? 'draft';

  /**
   * THE TWO THINGS THAT HAPPEN TO A REGISTER AFTER IT IS BUILT, and until now neither could.
   *
   * Every register ever produced carried a yellow "Draft" badge forever, so nothing on this screen
   * distinguished the fortnight the owner signed off from the one he was still working on. Both
   * doors invalidate the same query, so the badge and the buttons move together.
   */
  const afterwards = () =>
    void queryClient.invalidateQueries({ queryKey: detailKey('/admin/v1/payroll/runs', runId) });

  const approve = useMutation({
    mutationFn: () => rawApi('POST', `/admin/v1/payroll/runs/${runId}/approve`, {}),
    onSuccess: () => {
      notifyDone('Register signed off', 'The figures are fixed. It cannot be rebuilt from here.');
      afterwards();
    },
    onError: (err) => notifyError(err, 'sign this register off'),
  });

  /**
   * The way back, so signing off is not a trap.
   *
   * A signed-off register cannot be rebuilt, so without this the owner who signs the wrong fortnight
   * is holding a frozen wrong register — the exact thing this whole screen exists to stop him
   * trusting. Paid is a different matter and the server refuses it: the money has gone out.
   */
  const reopen = useMutation({
    mutationFn: () => rawApi('POST', `/admin/v1/payroll/runs/${runId}/reopen`, {}),
    onSuccess: () => {
      notifyDone('Back to a draft', 'Fix what is wrong and build the register again.');
      afterwards();
    },
    onError: (err) => notifyError(err, 'put this register back to a draft'),
  });

  const markPaid = useMutation({
    mutationFn: (input: { paidMethod: string; paidAt?: string }) =>
      rawApi('POST', `/admin/v1/payroll/runs/${runId}/pay`, input),
    onSuccess: () => {
      notifyDone('Marked paid', 'This pay period is closed. No more registers will be built for it.');
      afterwards();
      void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/payroll/periods') });
    },
    onError: (err) => notifyError(err, 'mark this register paid'),
  });

  const rebuild = useMutation({
    mutationFn: () =>
      rawApi<{ run: PayrollRun; lines: PayrollLine[] }>(
        'POST',
        `/admin/v1/payroll/periods/${data?.run.payrollPeriodId ?? ''}/runs`,
        {},
      ),
    onSuccess: (result) => {
      notifyDone('Payroll recalculated', 'The figures now use the current start and leaving dates.');
      // Old data can contain duplicate drafts. Recalculation deliberately retains the oldest run,
      // which may not be the one this page opened, so follow the server's surviving id.
      void navigate(`/payroll/runs/${result.run.id}`);
    },
    onError: (err) => notifyError(err, 'rebuild this register'),
  });

  const [paying, setPaying] = useState(false);

  /**
   * Split what the register reports into people it could not pay and people it paid with a caveat.
   *
   * Both arrive in the same `skipped` array, but some reasons stop the line being written
   * (`noCompensation`) while others are warnings attached to a line that WAS paid
   * (`unclosedShift`). Calling both "not paid" invites an operator to pay someone by hand who has
   * already been paid here. Partitioning on whether a line exists rather than on a hardcoded list
   * of reason codes keeps this correct when the backend adds a reason we have not seen.
   */
  const paidStaffIds = new Set(lines.map((l) => l.staffId));
  const reported = data?.run.skipped ?? [];
  const omitted = reported.filter((s) => !paidStaffIds.has(s.staffId));
  const warnings = reported.filter((s) => paidStaffIds.has(s.staffId));

  // Currencies are never summed together: this business pays in both lempiras and dollars, and a
  // single combined figure would be a confident lie.
  const totals = lines.reduce<Record<string, number>>((acc, line) => {
    acc[line.currency] = (acc[line.currency] ?? 0) + line.grossMinor;
    return acc;
  }, {});

  const periodLabel = data?.period
    ? `${formatDate(data.period.periodStart)} – ${formatDate(data.period.periodEnd)}`
    : '';

  return (
    <RecordScreen
      backTo="/payroll"
      backLabel="All pay periods"
      isLoading={runQuery.isLoading}
      notFound={runQuery.isError}
      notFoundMessage="This register could not be found."
      title="Pay register"
      subtitle={periodLabel || undefined}
      status={
        data?.run.status
          ? {
              label: enumLabel('payrollRunStatus', data.run.status),
              // Three states, three colours. It used to be two, so a register that had been signed
              // off and one that had actually been paid looked identical.
              color: status === 'draft' ? 'yellow' : status === 'finalized' ? 'blue' : 'teal',
            }
          : undefined
      }
      actions={
        <Group gap="xs" wrap="nowrap">
          <Button variant="light" onClick={() => window.print()}>
            Print
          </Button>
          {/*
            The two things that happen to a register after it is built, in the order they happen.
            Neither existed, so every register was a draft forever and nothing told the owner which
            fortnight he had signed and which he had paid.
          */}
          {status === 'draft' && (
            <Button
              loading={approve.isPending}
              disabled={data?.needsRebuild}
              onClick={() => approve.mutate()}
            >
              Sign this register off
            </Button>
          )}
          {status === 'finalized' && (
            <Button variant="subtle" loading={reopen.isPending} onClick={() => reopen.mutate()}>
              Back to a draft
            </Button>
          )}
          {status === 'finalized' && <Button onClick={() => setPaying(true)}>Mark as paid</Button>}
        </Group>
      }
      facts={[
        { label: 'People', value: String(lines.length) },
        ...Object.entries(totals).map(([currency, minor]) => ({
          label: `Total (${currency === 'HNL' ? 'lempiras' : 'dollars'})`,
          value: formatMoney(minor, currency),
        })),
        // Who signed, and who paid — by name. A directory id resolves to nobody here, which is why
        // the API records the name at the moment the button is pressed.
        ...(data?.run.finalizedByName
          ? [
              {
                label: 'Signed off by',
                value: `${data.run.finalizedByName}${
                  data.run.finalizedAt ? ` · ${formatDateTime(data.run.finalizedAt)}` : ''
                }`,
              },
            ]
          : []),
        ...(data?.run.paidAt
          ? [
              {
                label: 'Paid',
                value: `${formatDateTime(data.run.paidAt)}${
                  data.run.paidMethod ? ` · ${enumLabel('payrollPaidMethod', data.run.paidMethod)}` : ''
                }${data.run.paidByName ? ` · ${data.run.paidByName}` : ''}`,
              },
            ]
          : []),
      ]}
      sections={[
        {
          title: 'Gross pay',
          description: 'Before any deductions. Give this to the accountant.',
          children:
            lines.length === 0 ? (
              <Text size="sm" c="dimmed" fs="italic">
                No one was included in this register.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={780}>
                <Table striped highlightOnHover verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Person</Table.Th>
                      {/* The cell holds a rate — "L 120 / hour" — so the heading says rate.
                          "Basis" is the name of the column in the pay record, not a word an
                          accountant reading this page is looking for. */}
                      <Table.Th>Rate</Table.Th>
                      <Table.Th>Hours</Table.Th>
                      <Table.Th>Overtime</Table.Th>
                      <Table.Th>Adjustments</Table.Th>
                      <Table.Th ta="right">Gross</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {lines.map((line) => (
                      <Table.Tr key={line.id}>
                        <Table.Td fw={600}>{line.staffName ?? 'Unknown'}</Table.Td>
                        <Table.Td>
                          {line.holidayWorkedMinutes != null && (
                            <Text size="sm">
                              Holiday work {formatHours(line.holidayWorkedMinutes)} · +
                              {formatMoney(line.holidayPremiumMinor ?? 0, line.currency)}
                            </Text>
                          )}
                          {!!line.leaveBenefitLines?.length && (
                            <Text size="sm">
                              Leave wage adjustment{' '}
                              {formatMoney(line.leaveBenefitAdjustmentMinor ?? 0, line.currency)} ·{' '}
                              <Link to="/payroll?tab=benefits">benefit records</Link>
                            </Text>
                          )}
                          {line.leaveBenefitLines?.map((l) => (
                            <Text size="xs" key={l.leaveId}>
                              {l.kind}: owed {formatMoney(l.employerDueMinor, line.currency)}, included in
                              base {formatMoney(l.ordinaryIncludedMinor, line.currency)}, advance{' '}
                              {formatMoney(l.advanceMinor, line.currency)}
                            </Text>
                          ))}
                          {line.salaryMinor != null ? (
                            <Text size="sm">
                              {`Salary ${formatMoney(line.salaryMinor, line.currency)} / month`}
                              {/* THE TWO REASONS A GROSS IS NOT THE SALARY, and they are not the
                                  same reason.

                                  A period that is not a month buys a share of the salary — half of
                                  it twice a month — and that is not a caveat about the person, it
                                  is what the owner chose on the form. Printed as its own figure,
                                  because the one thing this document has to do is let an
                                  accountant reproduce the gross from what is on the page.

                                  Then, separately, somebody who started or left partway through is
                                  paid for the days they were actually here. That is the caveat,
                                  and it is measured against the period's share — otherwise every
                                  line of every half-month register would carry it and say
                                  something untrue about all of them. */}
                              {line.periodSalaryMinor != null &&
                                line.periodSalaryMinor !== line.salaryMinor && (
                                  <Text span size="xs" c="dimmed">
                                    {` · ${formatMoney(line.periodSalaryMinor, line.currency)} this period`}
                                  </Text>
                                )}
                              {line.grossMinor - (line.overtimePremiumMinor ?? 0) !==
                                (line.periodSalaryMinor ?? line.salaryMinor) && (
                                <Text span size="xs" c="dimmed">
                                  {' '}
                                  · part period
                                </Text>
                              )}
                            </Text>
                          ) : line.hourlyRateMinor != null ? (
                            `${formatMoney(line.hourlyRateMinor, line.currency)} / hour`
                          ) : (
                            '—'
                          )}
                        </Table.Td>
                        <Table.Td>{line.salaryMinor != null ? '—' : formatHours(line.hoursMinutes)}</Table.Td>
                        {/* Gross includes any overtime premium, so the premium has to be visible or
                            the figure cannot be reproduced from what is on the page — which is the
                            one thing an accountant needs this document to be able to do. */}
                        <Table.Td>
                          {line.overtimeMinutes ? (
                            <Text size="sm">
                              {formatHours(line.overtimeMinutes)}
                              {line.overtimePremiumMinor
                                ? ` · +${formatMoney(line.overtimePremiumMinor, line.currency)}${
                                    line.overtimePremiumBp ? ` (${formatRateBp(line.overtimePremiumBp)})` : ''
                                  }`
                                : ' · no premium'}
                            </Text>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        {/* The manual part of the gross, itemised — a register's one job is that
                            an accountant can reproduce its figure from what is on the page, and a
                            gross that quietly includes four docked days fails it. */}
                        <Table.Td>
                          {line.adjustmentsMinor != null ? (
                            <Text size="sm">
                              <Text span fw={600} c={line.adjustmentsMinor < 0 ? 'red' : 'teal'}>
                                {line.adjustmentsMinor < 0 ? '−' : '+'}
                                {formatMoney(Math.abs(line.adjustmentsMinor), line.currency)}
                              </Text>
                              {(line.adjustmentLines ?? []).map((a) => (
                                <Text key={a.adjustmentId} size="xs" c="dimmed">
                                  {a.note}
                                  {a.days ? ` · ${a.days} ${a.days === 1 ? 'day' : 'days'}` : ''}
                                </Text>
                              ))}
                            </Text>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td ta="right" fw={700}>
                          {formatMoney(line.grossMinor, line.currency)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ),
        },
      ]}
    >
      {data?.needsRebuild && status === 'draft' && (
        <Alert color="red" variant="light" title="These figures are out of date">
          <Group justify="space-between" align="center">
            <Text size="sm">
              Someone started or left after this draft was calculated. Rebuild it before relying on the
              amounts or signing it off.
            </Text>
            <Button size="compact-sm" loading={rebuild.isPending} onClick={() => rebuild.mutate()}>
              Recalculate payroll
            </Button>
          </Group>
        </Alert>
      )}

      {omitted.length > 0 && (
        <Paper withBorder radius="md" p="lg">
          <Title order={5} mb="xs">
            Not paid in this register
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            These people have no line above. Sort each one out and rebuild before sending this to the
            accountant.
          </Text>
          {omitted.map((s) => (
            <Alert key={s.staffId} color="red" variant="light" mb="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  {s.staffName ?? 'Someone'}
                </Text>
                {/* A code we have no sentence for still reads as words, never as camelCase. */}
                <Text size="sm">{SKIP_REASON[s.reason] ?? `${humanize(s.reason)} — check this one.`}</Text>
              </Group>
              <Button component={Link} to={`/staff/${s.staffId}`} size="compact-sm" variant="subtle" mt={4}>
                Open their record
              </Button>
            </Alert>
          ))}
        </Paper>
      )}

      {warnings.length > 0 && (
        <Paper withBorder radius="md" p="lg">
          <Title order={5} mb="xs">
            Paid, but worth checking
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            These people <strong>are</strong> on the register above and have been paid. Something about their
            hours is still worth a look — do not pay them again by hand.
          </Text>
          {warnings.map((s) => (
            <Alert key={s.staffId} color="yellow" variant="light" mb="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  {s.staffName ?? 'Someone'}
                </Text>
                <Text size="sm">{SKIP_REASON[s.reason] ?? `${humanize(s.reason)} — check this one.`}</Text>
              </Group>
              <Button component={Link} to={`/staff/${s.staffId}`} size="compact-sm" variant="subtle" mt={4}>
                Open their record
              </Button>
            </Alert>
          ))}
        </Paper>
      )}

      {/*
        The manual movements on this period's pay. On the register page rather than a page of their
        own because this is where the owner is when he notices the figures are not the payroll that
        happened — and the panel's Rebuild button closes the loop on the same screen.
      */}
      {data?.run.payrollPeriodId && (
        <AdjustmentsPanel periodId={data.run.payrollPeriodId} runStatus={status} onRebuilt={afterwards} />
      )}

      {paying && (
        <MarkPaidModal
          totals={totals}
          onClose={() => setPaying(false)}
          onConfirm={(input) => {
            markPaid.mutate(input, { onSuccess: () => setPaying(false) });
          }}
          saving={markPaid.isPending}
        />
      )}
    </RecordScreen>
  );
}

/**
 * Marking the register paid, which is also what closes the pay period.
 *
 * A confirmation rather than a bare button because it is the last thing that happens to a fortnight:
 * afterwards no register can be built for it again, and the figures on the page are what went out.
 * The totals are repeated here so the number being confirmed is the number in front of the person
 * confirming it.
 */
function MarkPaidModal({
  totals,
  onClose,
  onConfirm,
  saving,
}: {
  totals: Record<string, number>;
  onClose: () => void;
  onConfirm: (input: { paidMethod: string; paidAt?: string }) => void;
  saving: boolean;
}) {
  const [method, setMethod] = useState<string | null>('bankTransfer');
  // The bar's calendar, not the browser's: from 18:00 on the island Greenwich already says
  // tomorrow, and "paid tomorrow" is exactly the future date the server refuses.
  const today = useIslandToday();
  const [paidOn, setPaidOn] = useState(today);

  /*
   * Today means "just now" and sends nothing — the server stamps the moment. An earlier date is a
   * payday recorded after the fact, sent as noon on the island (UTC−6, no DST) so the date the
   * owner picked is the date every reader sees. The future is refused before it is sent.
   */
  const paidAt = paidOn && paidOn !== today ? `${paidOn}T12:00:00-06:00` : undefined;

  return (
    <Modal opened onClose={onClose} title="Mark this register paid" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          This records that the wages have gone out and closes the pay period. No other register can be built
          for these dates afterwards.
        </Text>
        <Paper withBorder p="sm" radius="sm">
          <Stack gap={2}>
            {Object.entries(totals).map(([currency, minor]) => (
              <Text key={currency} size="sm" fw={600}>
                {formatMoney(minor, currency)}
              </Text>
            ))}
            {Object.keys(totals).length === 0 && (
              <Text size="sm" c="dimmed">
                Nothing on this register.
              </Text>
            )}
          </Stack>
        </Paper>
        <Select
          label="How it was paid"
          data={enumOptions('payrollPaidMethod')}
          value={method}
          onChange={setMethod}
          comboboxProps={{ withinPortal: true }}
        />
        <TextInput
          type="date"
          label="When it was paid"
          description="Leave on today unless you are catching the books up to wages that already went out."
          max={today}
          value={paidOn}
          onChange={(e) => setPaidOn(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            loading={saving}
            disabled={!method || !paidOn || paidOn > today}
            onClick={() => method && onConfirm({ paidMethod: method, ...(paidAt ? { paidAt } : {}) })}
          >
            Yes, it has been paid
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
